import { useEffect, useState } from "react";
import { GetCustomerOccupations, GetCustomerOccupationsOnboarding } from "../functions/http-functions/customer";
import { useAuth } from "../state/context/auth.store";
import { OptionsType } from "../_types/form";
import { OccupationTypes } from "../_enums/occipationTypes.enum";

export interface RightToWorkItem {
	ID: number;
	Name: string;
	required?: boolean;
	hideFromOnboarding?: boolean;
	hasExpiry?: boolean;
	hasIssued?: boolean;
	hasLastChecked?: boolean;
}

const setDefaultOccupations = () => {
	return Object.values(OccupationTypes).map((occupation) => ({
		Name: occupation,
		ID: occupation,
	}));
};

export const useCustomerOccupations = () => {
	const { auth } = useAuth();
	const [occupationOptions, setCustomerOccupations] = useState<OptionsType[]>(setDefaultOccupations());
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const fetchDocuments = async () => {
			try {
				setIsLoading(true);
				const storedOccupations = sessionStorage.getItem("customerOccupations");
				if (storedOccupations) {
					setCustomerOccupations(JSON.parse(storedOccupations));
					setIsLoading(false);
					return;
				}

				const res = await GetCustomerOccupations(auth.token);
				if (res.data?.length) {
					const formattedOccupations = res.data
						?.filter?.((o) => o.visible)
						?.map((o) => ({
							Name: o.name,
							ID: o.name,
						}));
					setCustomerOccupations(formattedOccupations);
					sessionStorage?.setItem("customerOccupations", JSON.stringify(formattedOccupations));
				} else {
					setCustomerOccupations(setDefaultOccupations());
					sessionStorage?.setItem("customerOccupations", JSON.stringify(setDefaultOccupations()));
				}
			} catch (err) {
				setCustomerOccupations(setDefaultOccupations());
				sessionStorage?.setItem("customerOccupations", JSON.stringify(setDefaultOccupations()));
			} finally {
				setIsLoading(false);
			}
		};

		if (auth?.token) {
			fetchDocuments();
		}
	}, [auth?.token]);

	return { occupationOptions, isLoading };
};

export const useCustomerOccupationsOnboarding = (token: string) => {
	const [occupationOptions, setCustomerOccupations] = useState<OptionsType[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		const fetchDocuments = async () => {
			try {
				setIsLoading(true);
				const storedOccupations = sessionStorage?.getItem("customerOccupations");
				if (storedOccupations) {
					setCustomerOccupations(JSON.parse(storedOccupations));
					setIsLoading(false);
					return;
				}

				const res = await GetCustomerOccupationsOnboarding(token);
				if (res.data?.length) {
					const formattedOccupations = res.data
						?.filter?.((o) => o.visible)
						?.map((o) => ({
							Name: o.name,
							ID: o.name,
						}));
					setCustomerOccupations(formattedOccupations);
					sessionStorage?.setItem("customerOccupations", JSON.stringify(formattedOccupations));
				} else {
					setCustomerOccupations(setDefaultOccupations());
				}
			} catch (err) {
				setCustomerOccupations(setDefaultOccupations());
			} finally {
				setIsLoading(false);
			}
		};

		if (token) {
			fetchDocuments();
		}
	}, [token]);

	return { occupationOptions, isLoading };
};
